import { SearchOutlined } from "@ant-design/icons";
import { Input, notification, Table, Tabs } from "antd";
import { ColumnsType } from "antd/es/table";
import { CSSProperties, useEffect, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle } from "../../components/styled-components";
import { SupervisorDashboard } from "../../res/type";
import { GETV2 } from "../../services";
import { CHECKER_SUPERVISOR_DASHBOARD } from "../../services/path";

const Dashboard = () => {
  const [data, setData] = useState<SupervisorDashboard[]>([]);
  const [status, setStatus] = useState("cutting");
  const [searchParams, setSearchParams] = useState({
    searchTextUserID: "",
    searchTextUsername: "",
  });

  const allStatus = [
    { status: "Cutting", key: "cutting" },
    { status: "Pick", key: "picked" },
    { status: "Pack", key: "packed" },
  ];

  const columnStyle: CSSProperties = {
    alignItems: "center",
    textAlign: "center",
  };

  useEffect(() => {
    fetchData();
  }, [status]);

  const checkMatch = (fieldValue: string, searchValue: string) => {
    if (!searchValue) return true;
    const searchValues = searchValue.split(",,, ").map((val: string) => val.trim());

    return searchValues.some((val) => {
      return fieldValue.toLowerCase().includes(val.replace("*", "").toLowerCase());
    });
  };

  const fetchData = async () => {
    try {
      setData([]);
      const response = await GETV2(CHECKER_SUPERVISOR_DASHBOARD(status));
      setData(response?.result);
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.message ?? "Get supervisor dashboard fail!",
      });
    }
  };

  const handleTabChange = (status: string) => {
    setSearchParams((prev) => {
      const newParams = { ...prev, searchTextWord: "" };
      setTimeout(() => {
        setStatus(status);
      }, 0);
      return newParams;
    });
  };

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
  };

  const filteredData = data.filter((item) => {
    if (item.quantity !== "0" || item.average_time !== "0") {
      return (
        checkMatch(item.user_id ?? "", searchParams.searchTextUserID) &&
        checkMatch(item.firstname ?? "", searchParams.searchTextUsername)
      );
    }
  });

  const columns: ColumnsType<SupervisorDashboard> = [
    {
      width: "22%",
      title: (
        <div style={columnStyle}>
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            User ID :
          </span>
          <Input
            placeholder="Search UserID"
            value={searchParams.searchTextUserID}
            onChange={(e) => handleSearchInputChange("searchTextUserID", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "user_id",
      key: "user_id",
      sorter: (a: SupervisorDashboard, b: SupervisorDashboard) => {
        const A = a.user_id ?? "";
        const B = b.user_id ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      width: "22%",
      title: (
        <div style={columnStyle}>
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Name :
          </span>
          <Input
            placeholder="Search Name"
            value={searchParams.searchTextUsername}
            onChange={(e) => handleSearchInputChange("searchTextUsername", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "firstname",
      key: "firstname",
      sorter: (a: SupervisorDashboard, b: SupervisorDashboard) => {
        const A = a.firstname ?? "";
        const B = b.firstname ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text ? text : "-"}</span>
        </div>
      ),
    },
    {
      title: (
        <div style={columnStyle}>
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Quantity
          </span>
        </div>
      ),
      dataIndex: "quantity",
      key: "quantity",
      onCell: (record: SupervisorDashboard) => {
        const value = Number(record.quantity);
        const target = Number(record.target_quantity);
        const isPass = value >= target;
        return {
          style: {
            backgroundColor: isPass ? "#52c41a" : "#ff4d4f",
            color: "#fff",
            textAlign: "center",
          },
        };
      },
      render: (text: string, record: SupervisorDashboard) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text + " / " + record.target_quantity}</span>
        </div>
      ),
    },
    {
      width: "27%",
      title: (
        <div style={columnStyle}>
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "110px",
              maxWidth: "110px",
            }}
          >
            Average Time
          </span>
        </div>
      ),
      dataIndex: "average_time",
      key: "average_time",
      onCell: (record: SupervisorDashboard) => {
        const value = Number(record.average_time);
        const target = Number(record.target_time);
        const isPass = value < target;
        return {
          style: {
            backgroundColor: isPass ? "#52c41a" : "#ff4d4f",
            color: "#fff",
            textAlign: "center",
          },
        };
      },
      sorter: (a: SupervisorDashboard, b: SupervisorDashboard) => {
        const A = Number(a.average_time) || 0;
        const B = Number(b.average_time) || 0;
        return A - B;
      },
      render: (text: string, record: SupervisorDashboard) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text + " / " + record.target_time}</span>
        </div>
      ),
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>Supervisor Dashboard</h2>
        </HeaderTitle>
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "20px",
            borderRadius: "7px",
          }}
        >
          <Tabs onChange={handleTabChange} type="card">
            {allStatus.map((statusall) => (
              <Tabs.TabPane tab={statusall.status} key={statusall.key}>
                <div style={{ width: "100%", overflowX: "auto" }}>
                  <Table
                    columns={columns}
                    dataSource={filteredData}
                    scroll={{ x: 1500 }} // Adjust the value based on your needs
                    pagination={false}
                  />
                </div>
              </Tabs.TabPane>
            ))}
          </Tabs>
        </div>
      </ContentBox>
    </LoginedLayout>
  );
};

export default Dashboard;
